import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Checkbox, MenuItem, Paper, Tooltip, Typography } from '@mui/material'
import dayjs, { type Dayjs } from 'dayjs'

import { Icon } from '~components/icons'
import { lightTheme } from '~createTheme'
import { sanitizeText } from '~utils/textUtils'

import IconButton from '../IconButton'
import SearchBar from '../SearchBar'

import './AccessibleGrid.scss'
import CellDateInput from './CellDateInput'
import CellNumberInput from './CellNumberInput'
import CellSelectInput from './CellSelectInput'
import CellTextInput from './CellTextInput'

export type SortDirection = 'asc' | 'desc' | null

export interface AccessibleGridColumn {
  id: string
  headerName: string
  description?: string
  type: 'string' | 'number' | 'boolean' | 'date' | 'singleSelect' | 'multiSelect'
  required?: boolean
  editable?: boolean
  width?: number
  minWidth?: number
  options?: string[]
  optionLabels?: Record<string, string>
  sortable?: boolean
}

export interface AccessibleGridRow {
  id: string
  [key: string]: unknown
}

export interface AccessibleGridProps {
  columns: AccessibleGridColumn[]
  rows: AccessibleGridRow[]
  onCellChange: (rowId: string, columnId: string, value: unknown) => void
  onRowDelete?: (rowId: string) => void
  onAddRow?: () => void
  canAddMore?: boolean
  canDelete?: boolean
  isEditable?: boolean
  cellErrorMap?: Record<string, string>
  ariaLabel?: string
  ariaDescription?: string
  sortColumn?: string
  sortDirection?: SortDirection
  onSort?: (columnId: string, direction: SortDirection, sortedRows: AccessibleGridRow[]) => void
  onColumnResize?: (columnId: string, width: number) => void
}

const isBlankCellValue = (value: unknown): boolean =>
  value === null || value === undefined || (Array.isArray(value) && value.length === 0)

const toSelectedValues = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []

const getOptionLabel = (column: AccessibleGridColumn, option: string): string =>
  column.optionLabels?.[option] ?? option

const getSelectedLabels = (column: AccessibleGridColumn, value: unknown): string =>
  toSelectedValues(value)
    .map((option) => getOptionLabel(column, option))
    .join(', ')

export const sortAccessibleGridRows = (
  rows: AccessibleGridRow[],
  columns: AccessibleGridColumn[],
  columnId?: string,
  direction?: SortDirection,
): AccessibleGridRow[] => {
  if (!columnId || !direction) return rows

  const column = columns.find((c) => c.id === columnId)
  if (!column) return rows

  return [...rows].sort((a, b) => {
    const aVal = a[columnId]
    const bVal = b[columnId]

    const aBlank = isBlankCellValue(aVal)
    const bBlank = isBlankCellValue(bVal)
    if (aBlank && bBlank) return 0
    if (aBlank) return direction === 'asc' ? 1 : -1
    if (bBlank) return direction === 'asc' ? -1 : 1

    let comparison = 0

    switch (column.type) {
      case 'number':
        comparison = (Number(aVal) || 0) - (Number(bVal) || 0)
        break
      case 'date':
        comparison = dayjs(aVal as string).valueOf() - dayjs(bVal as string).valueOf()
        break
      case 'boolean':
        comparison = (aVal ? 1 : 0) - (bVal ? 1 : 0)
        break
      case 'multiSelect':
        comparison = getSelectedLabels(column, aVal).localeCompare(getSelectedLabels(column, bVal))
        break
      case 'string':
      case 'singleSelect':
      default:
        comparison = String(aVal).localeCompare(String(bVal))
        break
    }

    return direction === 'asc' ? comparison : -comparison
  })
}

interface CellEditorProps {
  value: unknown
  onChange: (value: unknown) => void
  column: AccessibleGridColumn
  isEditable: boolean
  isEditing: boolean
  onStartEdit: () => void
  onStopEdit: () => void
}

const StringCellEditor: React.FC<CellEditorProps> = ({
  value,
  onChange,
  column,
  isEditable,
  isEditing,
  onStartEdit,
}) => {
  if (!isEditable || !isEditing) {
    return (
      <Typography
        variant='body2'
        onClick={isEditable ? onStartEdit : undefined}
        className={`accessible-table-cell-value${isEditable ? ' accessible-table-cell-value--editable' : ''}`}
      >
        {(value as string) || ''}
      </Typography>
    )
  }

  return (
    <CellTextInput
      value={(value as string) || ''}
      onChange={(e) => onChange(e.target.value)}
      size='small'
      fullWidth
      autoFocus
      aria-label={column.headerName}
    />
  )
}

const NumberCellEditor: React.FC<CellEditorProps> = ({
  value,
  onChange,
  column,
  isEditable,
  isEditing,
  onStartEdit,
}) => {
  if (!isEditable || !isEditing) {
    return (
      <Typography
        variant='body2'
        onClick={isEditable ? onStartEdit : undefined}
        className={`accessible-table-cell-value${isEditable ? ' accessible-table-cell-value--editable' : ''}`}
      >
        {value != null ? String(value) : ''}
      </Typography>
    )
  }

  return (
    <CellNumberInput
      value={value as number}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      size='small'
      fullWidth
      autoFocus
      aria-label={column.headerName}
    />
  )
}

const BooleanCellEditor: React.FC<CellEditorProps> = ({ value, onChange, column, isEditable }) => {
  return (
    <Checkbox
      checked={Boolean(value)}
      onChange={(e) => onChange(e.target.checked)}
      disabled={!isEditable}
      tabIndex={isEditable ? 0 : -1}
      slotProps={{
        input: {
          'aria-label': `${column.headerName}, ${Boolean(value) ? 'checked' : 'unchecked'}`,
        },
      }}
    />
  )
}

const DateCellEditor: React.FC<CellEditorProps> = ({ value, onChange, isEditable, isEditing }) => {
  const dateValue = value ? dayjs(value as string) : null

  const handleChange = (newValue: Dayjs | null): void => {
    if (newValue && newValue.isValid()) {
      onChange(newValue.toISOString())
    } else {
      onChange(null)
    }
  }

  return (
    <CellDateInput
      value={dateValue}
      onChange={handleChange}
      disabled={!isEditable}
      autoFocus={isEditing}
    />
  )
}

const SelectCellEditor: React.FC<CellEditorProps> = ({
  value,
  onChange,
  column,
  isEditable,
  isEditing,
  onStartEdit,
  onStopEdit,
}) => {
  const multiple = column.type === 'multiSelect'
  const selectedValues = useMemo(() => toSelectedValues(value), [value])
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const cellRef = useRef<HTMLDivElement>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isEditing) {
      setOpen(true)
    }
  }, [isEditing])

  useEffect(() => {
    if (!open) {
      setSearchQuery('')
    }
  }, [open])

  const selectableOptions = useMemo(() => {
    const options = column.options || []
    if (multiple) {
      return [...selectedValues.filter((selected) => !options.includes(selected)), ...options]
    }
    if (value && typeof value === 'string' && !options.includes(value)) {
      return [value, ...options]
    }
    return options
  }, [column.options, value, multiple, selectedValues])

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return selectableOptions
    return selectableOptions.filter((option) =>
      getOptionLabel(column, option).toLowerCase().includes(searchQuery.toLowerCase()),
    )
  }, [selectableOptions, searchQuery, column])

  const closeMenu = (): void => {
    setOpen(false)
    setSearchQuery('')
    onStopEdit()
    setTimeout(() => cellRef.current?.closest('td')?.focus(), 0)
  }

  const handleSelect = (selectedValue: string): void => {
    if (multiple) {
      onChange(
        selectedValues.includes(selectedValue)
          ? selectedValues.filter((selected) => selected !== selectedValue)
          : [...selectedValues, selectedValue],
      )
      return
    }
    onChange(selectedValue)
    closeMenu()
  }

  const handleCellClick = (): void => {
    if (isEditable && !isEditing) {
      onStartEdit()
    } else if (isEditing && !open) {
      setOpen(true)
    }
  }

  const hasSearchBar = selectableOptions.length > 10

  useEffect((): void | (() => void) => {
    if (open && hasSearchBar) {
      const timer = setTimeout(() => {
        const input = searchContainerRef.current?.querySelector('input')
        input?.focus()
      }, 50)
      return (): void => clearTimeout(timer)
    }
  }, [open, hasSearchBar])

  const SearchPaper = useMemo(
    () =>
      React.forwardRef<HTMLDivElement, React.ComponentProps<typeof Paper>>(
        function SearchPaperInner({ children, ...props }, ref) {
          return (
            <Paper ref={ref} {...props}>
              <Box
                ref={searchContainerRef}
                role='search'
                aria-label='Search options'
                sx={{
                  px: 2,
                  py: 1,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: 'background.paper',
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    const menuList = searchContainerRef.current
                      ?.closest('[role="presentation"]')
                      ?.querySelector('[role="listbox"]')
                    if (menuList) {
                      const selectedItem = menuList.querySelector(
                        '[aria-selected="true"]',
                      ) as HTMLElement | null
                      const firstItem = menuList.querySelector(
                        '[role="option"]',
                      ) as HTMLElement | null
                      const target = selectedItem || firstItem
                      target?.focus()
                    }
                  } else if (e.key !== 'Escape') {
                    e.stopPropagation()
                  }
                }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <SearchBar placeholder='Search options...' onSearch={setSearchQuery} />
              </Box>
              {children}
            </Paper>
          )
        },
      ),
    [],
  )

  return (
    <Box
      ref={cellRef}
      onClick={handleCellClick}
      className='accessible-table-cell-wrapper'
      style={{ cursor: isEditable ? 'pointer' : 'default' }}
    >
      <CellSelectInput
        multiple={multiple}
        value={multiple ? selectedValues : (value as string) || ''}
        onChange={(e) => {
          if (multiple) {
            onChange(toSelectedValues(e.target.value))
          } else {
            handleSelect(e.target.value as string)
          }
        }}
        renderValue={
          multiple ? (selected): string => getSelectedLabels(column, selected) : undefined
        }
        ariaLabel={multiple ? getSelectedLabels(column, selectedValues) || 'blank' : undefined}
        fullWidth
        open={isEditing && open}
        onOpen={() => {
          if (isEditing) setOpen(true)
        }}
        onClose={closeMenu}
        disabled={!isEditable}
        MenuProps={
          hasSearchBar
            ? {
                autoFocus: false,
                disableAutoFocusItem: true,
                slotProps: {
                  paper: {
                    style: { maxHeight: 300 },
                    component: SearchPaper as React.ElementType,
                  },
                },
              }
            : { slotProps: { paper: { style: { maxHeight: 300 } } } }
        }
      >
        {searchQuery && filteredOptions.length === 0 && (
          <MenuItem disabled>
            <Typography variant='body2' color='textSecondary'>
              No results found
            </Typography>
          </MenuItem>
        )}
        {(searchQuery ? filteredOptions : selectableOptions).map((option) => (
          <MenuItem
            key={option}
            value={option}
            onKeyDown={(e) => {
              if (e.key === ' ') {
                e.preventDefault()
                handleSelect(option)
              }
            }}
          >
            {multiple && (
              <Checkbox
                checked={selectedValues.includes(option)}
                size='small'
                tabIndex={-1}
                sx={{ p: 0, mr: 1 }}
              />
            )}
            {getOptionLabel(column, option)}
          </MenuItem>
        ))}
      </CellSelectInput>
    </Box>
  )
}

const CellEditor: React.FC<CellEditorProps> = (props) => {
  const { column } = props

  switch (column.type) {
    case 'string':
      return <StringCellEditor {...props} />
    case 'number':
      return <NumberCellEditor {...props} />
    case 'boolean':
      return <BooleanCellEditor {...props} />
    case 'date':
      return <DateCellEditor {...props} />
    case 'singleSelect':
    case 'multiSelect':
      return <SelectCellEditor {...props} />
    default:
      return <StringCellEditor {...props} />
  }
}

interface HeaderCellProps {
  column: AccessibleGridColumn
  colIndex: number
  onKeyDown: (e: React.KeyboardEvent, row: number, col: number) => void
  headerId: string
  sortDirection?: SortDirection
  onSort?: (columnId: string, direction: SortDirection) => void
  onResize?: (columnId: string, width: number) => void
  currentWidth?: number
}

const getAccessibleDescription = (sanitizedHtml: string): { text: string; hasContent: boolean } => {
  if (!sanitizedHtml) return { text: '', hasContent: false }
  const { body } = new DOMParser().parseFromString(sanitizedHtml, 'text/html')
  const hasImage = body.querySelector('img') !== null
  body.querySelectorAll('img').forEach((img) => img.replaceWith(img.alt))
  body.querySelectorAll('p, br, li, div, h1, h2, h3, h4, h5, h6').forEach((el) => el.after(' '))
  const text = (body.textContent ?? '').replace(/\s+/g, ' ').trim()
  return { text, hasContent: hasImage || text.length > 0 }
}

const HeaderCell: React.FC<HeaderCellProps> = ({
  column,
  colIndex,
  onKeyDown,
  headerId,
  sortDirection,
  onSort,
  onResize,
  currentWidth,
}) => {
  const textRef = useRef<HTMLSpanElement>(null)
  const thRef = useRef<HTMLTableCellElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const sanitizedDescription = column.description ? sanitizeText(column.description) : ''
  const { text: plainDescription, hasContent: hasDescription } = useMemo(
    () => getAccessibleDescription(sanitizedDescription),
    [sanitizedDescription],
  )

  useEffect(() => {
    const checkTruncation = (): void => {
      if (textRef.current) {
        setIsTruncated(
          textRef.current.scrollWidth > textRef.current.clientWidth ||
            textRef.current.scrollHeight > textRef.current.clientHeight,
        )
      }
    }
    checkTruncation()
    window.addEventListener('resize', checkTruncation)
    return (): void => window.removeEventListener('resize', checkTruncation)
  }, [column.headerName, currentWidth])

  const handleSort = (e: React.MouseEvent | React.KeyboardEvent): void => {
    e.stopPropagation()
    if (!onSort || column.sortable === false) return

    let newDirection: SortDirection
    if (sortDirection === 'asc') {
      newDirection = 'desc'
    } else if (sortDirection === 'desc') {
      newDirection = null
    } else {
      newDirection = 'asc'
    }
    onSort(column.id, newDirection)
  }

  const handleResizeMouseDown = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    if (!onResize || !thRef.current) return

    const startX = e.clientX
    const startWidth = thRef.current.offsetWidth
    const th = thRef.current

    th.classList.add('accessible-table-th--resizing')
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const handleMouseMove = (moveEvent: MouseEvent): void => {
      const newWidth = Math.max(column.minWidth || 50, startWidth + (moveEvent.clientX - startX))
      th.style.width = `${newWidth}px`
    }

    const handleMouseUp = (upEvent: MouseEvent): void => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      th.classList.remove('accessible-table-th--resizing')
      document.body.style.cursor = ''
      document.body.style.userSelect = ''

      const finalWidth = Math.max(column.minWidth || 50, startWidth + (upEvent.clientX - startX))
      onResize(column.id, finalWidth)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const getSortIcon = (): string => {
    if (sortDirection === 'asc') return 'KeyboardArrowUp'
    if (sortDirection === 'desc') return 'KeyboardArrowDown'
    return 'UnfoldMore'
  }

  return (
    <th
      ref={thRef}
      id={headerId}
      className='accessible-table-th'
      scope='col'
      aria-label={`${column.headerName}${column.required ? ', required' : ''}${
        plainDescription ? `. ${plainDescription}` : ''
      }`}
      aria-sort={
        sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : undefined
      }
      style={{
        width: currentWidth ? `${currentWidth}px` : column.width ? `${column.width}px` : 'auto',
        minWidth: column.minWidth !== undefined ? `${column.minWidth}px` : '100px',
      }}
      data-row={-1}
      data-col={colIndex}
      tabIndex={0}
      onKeyDown={(e) => onKeyDown(e, -1, colIndex)}
      onClick={column.sortable !== false ? handleSort : undefined}
    >
      <div className='accessible-table-header-wrapper' aria-hidden='true'>
        <Tooltip
          title={column.headerName}
          placement='top'
          arrow
          disableHoverListener={!isTruncated}
          slotProps={{
            popper: {
              'aria-hidden': true,
            } as React.HTMLAttributes<HTMLDivElement>,
          }}
        >
          <div className='accessible-table-header-content' aria-hidden='true'>
            <span ref={textRef} className='accessible-table-header-text'>
              {column.headerName}
            </span>
            {column.required && <span className='accessible-table-required'>*</span>}
          </div>
        </Tooltip>
        {hasDescription && (
          <Tooltip
            title={
              <Typography
                variant='body2'
                component='div'
                className='accessible-table-header-description'
                dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
              />
            }
            placement='top'
            arrow
            slotProps={{
              popper: {
                'aria-hidden': true,
              } as React.HTMLAttributes<HTMLDivElement>,
            }}
          >
            <span
              className='accessible-table-header-help'
              aria-hidden='true'
              onClick={(e) => e.stopPropagation()}
            >
              <Icon name='help' size={16} fill={0} aria-hidden='true' />
            </span>
          </Tooltip>
        )}
        {column.sortable !== false && onSort && (
          <button
            className={`accessible-table-sort-button ${sortDirection ? 'accessible-table-sort-button--active' : ''}`}
            onClick={handleSort}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleSort(e)
              }
            }}
            aria-hidden='true'
            tabIndex={-1}
          >
            <Icon name={getSortIcon()} size={16} aria-hidden='true' />
          </button>
        )}
      </div>
      {onResize && (
        <div
          className='accessible-table-resize-handle'
          onMouseDown={handleResizeMouseDown}
          aria-orientation='vertical'
          aria-hidden={true}
        />
      )}
    </th>
  )
}

export const AccessibleGrid: React.FC<AccessibleGridProps> = ({
  columns,
  rows,
  onCellChange,
  onRowDelete,
  onAddRow,
  canAddMore = false,
  canDelete = false,
  isEditable = true,
  cellErrorMap = {},
  ariaLabel = 'Data table',
  ariaDescription,
  sortColumn,
  sortDirection,
  onSort,
  onColumnResize,
}) => {
  const tableRef = useRef<HTMLTableElement>(null)
  const addButtonRef = useRef<HTMLButtonElement>(null)
  const [focusedCell, setFocusedCell] = useState<{ row: number; col: number } | null>(null)
  const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null)
  const [internalSortColumn, setInternalSortColumn] = useState<string | undefined>(sortColumn)
  const [internalSortDirection, setInternalSortDirection] = useState<SortDirection>(
    sortDirection ?? null,
  )
  useEffect(() => {
    setInternalSortColumn(sortColumn)
    setInternalSortDirection(sortDirection ?? null)
  }, [sortColumn, sortDirection])
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const widths: Record<string, number> = {}
    columns.forEach((col) => {
      if (col.width) widths[col.id] = col.width
    })
    return widths
  })

  const handleInternalSort = useCallback(
    (columnId: string, direction: SortDirection) => {
      setInternalSortColumn(direction ? columnId : undefined)
      setInternalSortDirection(direction)
      onSort?.(columnId, direction, sortAccessibleGridRows(rows, columns, columnId, direction))
    },
    [rows, columns, onSort],
  )

  const handleInternalResize = useCallback(
    (columnId: string, width: number) => {
      setColumnWidths((prev) => ({ ...prev, [columnId]: width }))
      onColumnResize?.(columnId, width)
    },
    [onColumnResize],
  )

  const sortedRows = useMemo(
    () => sortAccessibleGridRows(rows, columns, internalSortColumn, internalSortDirection),
    [rows, columns, internalSortColumn, internalSortDirection],
  )

  const startEditing = useCallback((rowIndex: number, colIndex: number) => {
    setEditingCell({ row: rowIndex, col: colIndex })
  }, [])

  const stopEditing = useCallback(() => {
    setEditingCell(null)
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, rowIndex: number, colIndex: number) => {
      const totalRows = rows.length
      const totalCols = columns.length + 1
      const isCurrentlyEditing = editingCell?.row === rowIndex && editingCell?.col === colIndex
      const isDataCell = rowIndex >= 0

      if (e.key === 'Escape' && isCurrentlyEditing) {
        e.preventDefault()
        stopEditing()
        const cell = tableRef.current?.querySelector(
          `[data-row="${rowIndex}"][data-col="${colIndex}"]`,
        ) as HTMLElement
        cell?.focus()
        return
      }

      if (e.key === 'Enter' && isCurrentlyEditing) {
        const column = columns[colIndex]
        const activeElement = document.activeElement
        const isInDatePicker =
          activeElement?.closest('button[aria-label*="calendar"]') ||
          activeElement?.closest('.MuiIconButton-root') ||
          activeElement?.closest('.MuiPickersPopper-root') ||
          activeElement?.closest('.MuiDateCalendar-root')

        if (column?.type === 'date' && isInDatePicker) {
          return
        }

        e.preventDefault()
        stopEditing()
        const cell = tableRef.current?.querySelector(
          `[data-row="${rowIndex}"][data-col="${colIndex}"]`,
        ) as HTMLElement
        cell?.focus()
        return
      }

      if (e.key === 'Tab' && isCurrentlyEditing) {
        const column = columns[colIndex]
        const activeElement = document.activeElement
        const cell = tableRef.current?.querySelector(
          `[data-row="${rowIndex}"][data-col="${colIndex}"]`,
        ) as HTMLElement

        const focusableElements = cell?.querySelectorAll(
          'input, button, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        const focusableArray = focusableElements ? Array.from(focusableElements) : []
        const currentIndex = focusableArray.indexOf(activeElement as Element)

        if (focusableArray.length > 1) {
          if (!e.shiftKey && currentIndex < focusableArray.length - 1) {
            return
          }
          if (e.shiftKey && currentIndex > 0) {
            return
          }
        }

        if (column?.type === 'date') {
          const isOnCalendarButton =
            activeElement?.closest('button[aria-label*="date"]') ||
            activeElement?.closest('button[aria-label*="calendar"]') ||
            activeElement?.closest('.MuiIconButton-root')

          if (!isOnCalendarButton && !e.shiftKey) {
            return
          }
        }

        stopEditing()

        if (e.shiftKey) {
          if (colIndex > 0) {
            e.preventDefault()
            const cell = tableRef.current?.querySelector(
              `[data-row="${rowIndex}"][data-col="${colIndex - 1}"]`,
            ) as HTMLElement
            cell?.focus()
          } else if (rowIndex > 0) {
            e.preventDefault()
            const cell = tableRef.current?.querySelector(
              `[data-row="${rowIndex - 1}"][data-col="${totalCols - 1}"]`,
            ) as HTMLElement
            cell?.focus()
          }
          return
        }

        e.preventDefault()

        const isLastCell = rowIndex === totalRows - 1 && colIndex === totalCols - 1
        if (isLastCell) {
          if (canAddMore && addButtonRef.current) {
            addButtonRef.current.focus()
          }
          return
        }

        if (colIndex < totalCols - 1) {
          const cell = tableRef.current?.querySelector(
            `[data-row="${rowIndex}"][data-col="${colIndex + 1}"]`,
          ) as HTMLElement
          cell?.focus()
        } else if (rowIndex < totalRows - 1) {
          const cell = tableRef.current?.querySelector(
            `[data-row="${rowIndex + 1}"][data-col="${0}"]`,
          ) as HTMLElement
          cell?.focus()
        }
        return
      }

      if ((e.key === ' ' || e.key === 'Enter') && isDataCell && !isCurrentlyEditing) {
        e.preventDefault()
        startEditing(rowIndex, colIndex)
        return
      }

      if (
        isDataCell &&
        !isCurrentlyEditing &&
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        e.preventDefault()
        const row = sortedRows[rowIndex]
        const column = columns[colIndex]
        if (row && column && column.type !== 'date' && column.type !== 'multiSelect') {
          onCellChange(row.id, column.id, e.key)
        }
        startEditing(rowIndex, colIndex)
        return
      }

      if (isCurrentlyEditing) {
        return
      }

      let newRow = rowIndex
      let newCol = colIndex

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault()
          newRow = Math.max(-1, rowIndex - 1)
          break
        case 'ArrowDown':
          e.preventDefault()
          newRow = Math.min(totalRows - 1, rowIndex + 1)
          break
        case 'ArrowLeft':
          e.preventDefault()
          newCol = Math.max(0, colIndex - 1)
          break
        case 'ArrowRight':
          e.preventDefault()
          newCol = Math.min(totalCols - 1, colIndex + 1)
          break
        case 'Tab':
          if (e.shiftKey) {
            if (colIndex > 0) {
              e.preventDefault()
              newCol = colIndex - 1
            } else if (rowIndex > -1) {
              e.preventDefault()
              newRow = rowIndex - 1
              newCol = totalCols - 1
            }
          } else {
            if (colIndex < totalCols - 1) {
              e.preventDefault()
              newCol = colIndex + 1
            } else if (rowIndex < totalRows - 1) {
              e.preventDefault()
              newRow = rowIndex + 1
              newCol = 0
            } else {
              if (canAddMore && addButtonRef.current) {
                e.preventDefault()
                addButtonRef.current.focus()
              }
            }
          }
          break
        default:
          return
      }

      if (newRow !== rowIndex || newCol !== colIndex) {
        stopEditing()
        setFocusedCell({ row: newRow, col: newCol })
      }
    },
    [sortedRows, columns, canAddMore, editingCell, startEditing, stopEditing, onCellChange],
  )

  useEffect(() => {
    if (focusedCell && tableRef.current) {
      const cell = tableRef.current.querySelector(
        `[data-row="${focusedCell.row}"][data-col="${focusedCell.col}"]`,
      ) as HTMLElement

      if (cell) {
        cell.focus()
      }
    }
  }, [focusedCell])

  const getCellError = (rowId: string, columnId: string): string | undefined => {
    const errorKey = `${rowId}-${columnId}`
    return cellErrorMap[errorKey]
  }

  const cssVariables = {
    '--table-primary-main': lightTheme.palette.primary.main,
    '--table-primary-contrast': lightTheme.palette.primary.contrastText,
    '--table-secondary-main': lightTheme.palette.secondary.main,
    '--table-secondary-dark': lightTheme.palette.secondary.dark,
    '--table-error-main': lightTheme.palette.error.main,
    '--table-background-default': lightTheme.palette.background.default,
    '--table-background-paper': lightTheme.palette.background.paper,
    '--table-action-hover': lightTheme.palette.action.hover,
    '--table-action-disabled': lightTheme.palette.action.disabled,
    '--table-text-secondary': lightTheme.palette.text.secondary,
    '--table-text-disabled': lightTheme.palette.text.disabled,
    '--table-divider': lightTheme.palette.divider,
    '--table-border-radius': `${lightTheme.shape.borderRadius}px`,
    '--table-input-hover': lightTheme.palette.input?.hover || lightTheme.palette.primary.light,
    '--table-input-focus': lightTheme.palette.input?.focus || lightTheme.palette.primary.main,
  } as React.CSSProperties

  const gridId = React.useId()
  return (
    <div className='accessible-table-container' style={cssVariables}>
      <table
        ref={tableRef}
        className='accessible-table'
        role='grid'
        aria-label={ariaLabel}
        aria-describedby={ariaDescription ? gridId + '-table-description' : undefined}
        aria-rowcount={rows.length + 1}
        aria-colcount={columns.length + 1}
      >
        {ariaDescription && (
          <caption id={gridId + '-table-description'} className='sr-only'>
            {ariaDescription}
          </caption>
        )}
        <thead className='accessible-table-thead'>
          <tr role='row'>
            {columns.map((column, colIndex) => (
              <HeaderCell
                key={column.id}
                column={column}
                colIndex={colIndex}
                onKeyDown={handleKeyDown}
                headerId={`header-${column.id}`}
                sortDirection={internalSortColumn === column.id ? internalSortDirection : undefined}
                onSort={handleInternalSort}
                onResize={handleInternalResize}
                currentWidth={columnWidths[column.id]}
              />
            ))}
            <th
              id={gridId + '-header-actions'}
              className='accessible-table-th accessible-table-th--action'
              scope='col'
              aria-label='Actions'
              data-row={-1}
              data-col={columns.length}
              tabIndex={0}
              onKeyDown={(e) => handleKeyDown(e, -1, columns.length)}
            />
          </tr>
        </thead>
        <tbody className='accessible-table-tbody'>
          {sortedRows.map((row, rowIndex) => (
            <tr
              key={row.id}
              className={`accessible-table-tr${rowIndex % 2 === 0 ? ' accessible-table-tr--even' : ''}`}
              role='row'
              aria-rowindex={rowIndex + 2}
            >
              {columns.map((column, colIndex) => {
                const error = getCellError(row.id, column.id)
                const isSelectCell =
                  (column.type === 'singleSelect' || column.type === 'multiSelect') &&
                  isEditable &&
                  column.editable !== false
                return (
                  <td
                    key={`${row.id}-${column.id}`}
                    className={`accessible-table-td${error ? ' accessible-table-td--error' : ''}${isSelectCell ? ' accessible-table-td--select' : ''}`}
                    role='gridcell'
                    headers={`header-${column.id}`}
                    aria-colindex={colIndex + 1}
                    aria-describedby={error ? `error-${row.id}-${column.id}` : undefined}
                    data-row={rowIndex}
                    data-col={colIndex}
                    tabIndex={-1}
                    onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                    onClick={
                      isSelectCell ? (): void => startEditing(rowIndex, colIndex) : undefined
                    }
                  >
                    <CellEditor
                      value={row[column.id]}
                      onChange={(value) => onCellChange(row.id, column.id, value)}
                      column={column}
                      isEditable={isEditable && column.editable !== false}
                      isEditing={editingCell?.row === rowIndex && editingCell?.col === colIndex}
                      onStartEdit={() => startEditing(rowIndex, colIndex)}
                      onStopEdit={stopEditing}
                    />
                    {error && (
                      <span id={`error-${row.id}-${column.id}`} className='sr-only' role='alert'>
                        Error: {error}
                      </span>
                    )}
                  </td>
                )
              })}
              <td
                className='accessible-table-action-cell'
                role='gridcell'
                headers={gridId + '-header-actions'}
                aria-colindex={columns.length + 1}
                data-row={rowIndex}
                data-col={columns.length}
                tabIndex={-1}
                onKeyDown={(e) => handleKeyDown(e, rowIndex, columns.length)}
              >
                {canDelete && onRowDelete && sortedRows.length > 1 && (
                  <IconButton
                    onClick={() => onRowDelete(row.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                        onRowDelete(row.id)
                      }
                    }}
                    aria-label={`Delete row ${rowIndex + 1}`}
                    square='24px'
                    showOutline={false}
                    tabIndex={0}
                  >
                    <Icon name='Close' size={16} />
                  </IconButton>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canAddMore && onAddRow && isEditable && (
        <div className='accessible-table-add-row'>
          <IconButton
            color='primary'
            ref={addButtonRef}
            onClick={onAddRow}
            aria-label='Add new row'
            square='24px'
            className='accessible-table-add-button'
          >
            <Icon name='Add' size={24} color={lightTheme.palette.primary.contrastText} />
          </IconButton>
        </div>
      )}
    </div>
  )
}

export default AccessibleGrid
